import { useState } from 'react';
import { supabase } from '../supabase';
import { useTheme } from '../ThemeContext';

const FEEDBACK_TYPES = [
  { key: 'bug',     label: 'Bug Report',       emoji: '🐛' },
  { key: 'feature', label: 'Feature Request',  emoji: '💡' },
  { key: 'general', label: 'General Feedback', emoji: '💬' },
];

export default function FeedbackButton({ session, companyId }) {
  const { t } = useTheme();
  const [open, setOpen]           = useState(false);
  const [type, setType]           = useState('general');
  const [message, setMessage]     = useState('');
  const [email, setEmail]         = useState(session?.user?.email ?? '');
  const [rating, setRating]       = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [submitting, setSubmitting]   = useState(false);
  const [done, setDone]           = useState(false);

  const reset = () => {
    setType('general');
    setMessage('');
    setEmail(session?.user?.email ?? '');
    setRating(0);
    setHoverRating(0);
    setDone(false);
  };

  const handleOpen = () => {
    reset();
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setTimeout(reset, 300);
  };

  const handleSubmit = async () => {
    if (!message.trim()) return;
    setSubmitting(true);
    try {
      const { data, error } = await supabase.functions.invoke('send-feedback', {
        body: {
          company_id: companyId,
          feedback_type: type,
          message: message.trim(),
          user_email: email.trim() || null,
          rating: rating || null,
        },
      });
      if (error || data?.error) throw error || new Error(data.error);

      setDone(true);
    } catch (err) {
      console.error('[Feedback] submit error:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const displayRating = hoverRating || rating;

  return (
    <>
      {/* Floating button */}
      <button
        onClick={handleOpen}
        title="Give feedback"
        style={{
          position: 'fixed',
          bottom: 80,
          right: 20,
          zIndex: 1200,
          width: 44,
          height: 44,
          borderRadius: '50%',
          backgroundColor: '#D97706',
          border: 'none',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: '0 4px 14px rgba(217,119,6,0.45)',
          transition: 'transform 0.15s ease, box-shadow 0.15s ease',
        }}
        onMouseEnter={e => { e.currentTarget.style.transform = 'scale(1.08)'; e.currentTarget.style.boxShadow = '0 6px 20px rgba(217,119,6,0.55)'; }}
        onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; e.currentTarget.style.boxShadow = '0 4px 14px rgba(217,119,6,0.45)'; }}
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/>
        </svg>
      </button>

      {/* Backdrop */}
      {open && (
        <div
          onClick={handleClose}
          style={{
            position: 'fixed', inset: 0, zIndex: 1300,
            backgroundColor: 'rgba(0,0,0,0.35)',
          }}
        />
      )}

      {/* Modal */}
      {open && (
        <div
          style={{
            position: 'fixed',
            bottom: 136,
            right: 20,
            zIndex: 1400,
            width: 340,
            backgroundColor: t.card,
            borderRadius: 16,
            boxShadow: '0 8px 40px rgba(0,0,0,0.2)',
            border: `1px solid ${t.border1}`,
            fontFamily: "'Inter', system-ui, sans-serif",
            overflow: 'hidden',
          }}
        >
          {/* Header */}
          <div style={{
            padding: '16px 20px 14px',
            borderBottom: `1px solid ${t.border1}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}>
            <div>
              <p style={{ margin: 0, fontSize: 15, fontWeight: 700, color: t.text1 }}>
                How can we improve TrackShop?
              </p>
              <p style={{ margin: '2px 0 0', fontSize: 12, color: t.text4 }}>
                We read every piece of feedback
              </p>
            </div>
            <button
              onClick={handleClose}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: t.text4, fontSize: 18, lineHeight: 1, padding: '2px 4px' }}
              aria-label="Close"
            >✕</button>
          </div>

          {done ? (
            <div style={{ padding: '36px 20px', textAlign: 'center' }}>
              <div style={{ fontSize: 40, lineHeight: 1, marginBottom: 12 }}>🎉</div>
              <p style={{ margin: '0 0 6px', fontSize: 16, fontWeight: 700, color: t.text1 }}>
                Thanks for your feedback!
              </p>
              <p style={{ margin: 0, fontSize: 13, color: t.text3, lineHeight: 1.6 }}>
                We genuinely appreciate you taking the time. We'll use this to make TrackShop better.
              </p>
              <button
                onClick={handleClose}
                style={{
                  marginTop: 20, padding: '9px 24px',
                  backgroundColor: '#D97706', color: '#fff',
                  border: 'none', borderRadius: 8,
                  fontSize: 13, fontWeight: 700, cursor: 'pointer',
                }}
              >
                Close
              </button>
            </div>
          ) : (
            <div style={{ padding: '16px 20px 20px' }}>
              {/* Feedback type */}
              <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
                {FEEDBACK_TYPES.map((ft) => (
                  <button
                    key={ft.key}
                    onClick={() => setType(ft.key)}
                    style={{
                      flex: 1,
                      padding: '8px 4px',
                      borderRadius: 8,
                      border: `1.5px solid ${type === ft.key ? '#D97706' : t.border1}`,
                      backgroundColor: type === ft.key ? '#FFFBEB' : t.card2,
                      cursor: 'pointer',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: 3,
                      transition: 'border-color 0.15s ease, background-color 0.15s ease',
                    }}
                  >
                    <span style={{ fontSize: 18 }}>{ft.emoji}</span>
                    <span style={{ fontSize: 10, fontWeight: 600, color: type === ft.key ? '#92400E' : t.text3, textAlign: 'center', lineHeight: 1.3 }}>
                      {ft.label}
                    </span>
                  </button>
                ))}
              </div>

              {/* Message */}
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="What's on your mind? Be as specific as you like…"
                maxLength={5000}
                rows={4}
                style={{
                  width: '100%',
                  boxSizing: 'border-box',
                  padding: '10px 12px',
                  borderRadius: 8,
                  border: `1px solid ${t.borderInput}`,
                  backgroundColor: t.card2,
                  color: t.text1,
                  fontSize: 13,
                  fontFamily: 'inherit',
                  resize: 'vertical',
                  outline: 'none',
                  lineHeight: 1.6,
                  marginBottom: 12,
                }}
              />

              {/* Email */}
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Your email (optional)"
                style={{
                  width: '100%',
                  boxSizing: 'border-box',
                  padding: '9px 12px',
                  borderRadius: 8,
                  border: `1px solid ${t.borderInput}`,
                  backgroundColor: t.card2,
                  color: t.text1,
                  fontSize: 13,
                  fontFamily: 'inherit',
                  outline: 'none',
                  marginBottom: 14,
                }}
              />

              {/* Star rating */}
              <div style={{ marginBottom: 16 }}>
                <p style={{ margin: '0 0 6px', fontSize: 12, fontWeight: 600, color: t.text3 }}>
                  Overall satisfaction
                </p>
                <div style={{ display: 'flex', gap: 6 }}>
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={star}
                      onClick={() => setRating(star)}
                      onMouseEnter={() => setHoverRating(star)}
                      onMouseLeave={() => setHoverRating(0)}
                      style={{
                        background: 'none', border: 'none', cursor: 'pointer',
                        fontSize: 24, lineHeight: 1, padding: 0,
                        color: star <= displayRating ? '#F59E0B' : t.border1,
                        transition: 'color 0.1s ease',
                      }}
                      aria-label={`${star} star`}
                    >
                      ★
                    </button>
                  ))}
                </div>
              </div>

              {/* Submit */}
              <button
                onClick={handleSubmit}
                disabled={submitting || !message.trim()}
                style={{
                  width: '100%',
                  padding: '11px',
                  backgroundColor: !message.trim() ? t.border1 : '#D97706',
                  color: !message.trim() ? t.text4 : '#fff',
                  border: 'none',
                  borderRadius: 8,
                  fontSize: 14,
                  fontWeight: 700,
                  cursor: !message.trim() ? 'not-allowed' : 'pointer',
                  transition: 'background-color 0.15s ease',
                }}
              >
                {submitting ? 'Sending…' : 'Send Feedback'}
              </button>
            </div>
          )}
        </div>
      )}
    </>
  );
}
